import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const VENDOR_OPTIONS = [
  'Venue',
  'Planner/Coordinator',
  'Photographer',
  'Videographer',
  'DJ/Band',
  'Caterer',
  'Florist',
  'Rentals',
  'Officiant',
  'Bakery',
  'Hair/Makeup',
  'Transportation',
  'Other'
];

const PLANNING_STAGES = [
  'Just starting',
  'Booked some vendors',
  'Mostly planned',
  'Finalizing'
];

const WeddingInsightIntakePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const purchaseId = searchParams.get('purchase_id');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    wedding_date: '',
    venue: '',
    guest_count: '',
    planning_stage: '',
    vendors_booked: [],
    biggest_concern: '',
    additional_notes: '',
    ceremony_start_time: '',
    reception_end_time: '',
    first_look: ''
  });

  useEffect(() => {
    const fetchPurchase = async () => {
      if (!purchaseId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/wedding-insight/purchase/${purchaseId}`);
        setPurchase(response.data);
        
        // Pre-fill email and name if available
        if (response.data.email) {
          setFormData(prev => ({ ...prev, email: response.data.email }));
        }
        if (response.data.name) {
          setFormData(prev => ({ ...prev, name: response.data.name }));
        }
        
        // Check if intake already submitted
        if (response.data.intake_submitted) {
          setSubmitted(true);
        }
      } catch (error) {
        console.error('Error fetching purchase:', error);
        toast.error('Unable to load your purchase information');
      } finally {
        setLoading(false);
      }
    };

    fetchPurchase();
  }, [purchaseId]);

  const handleVendorToggle = (vendor) => {
    setFormData(prev => ({
      ...prev,
      vendors_booked: prev.vendors_booked.includes(vendor)
        ? prev.vendors_booked.filter(v => v !== vendor)
        : [...prev.vendors_booked, vendor]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    if (!formData.wedding_date) {
      toast.error('Please enter your wedding date');
      return;
    }
    if (!formData.venue.trim()) {
      toast.error('Please enter your venue information');
      return;
    }
    if (!formData.guest_count) {
      toast.error('Please enter your estimated guest count');
      return;
    }
    if (!formData.planning_stage) {
      toast.error('Please select your planning stage');
      return;
    }
    if (!formData.biggest_concern.trim()) {
      toast.error('Please tell us your biggest concern');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/wedding-insight/intake`, {
        purchase_id: purchaseId,
        name: formData.name,
        email: formData.email,
        wedding_date: formData.wedding_date,
        venue: formData.venue,
        guest_count: parseInt(formData.guest_count),
        planning_stage: formData.planning_stage,
        vendors_booked: formData.vendors_booked,
        biggest_concern: formData.biggest_concern,
        additional_notes: formData.additional_notes || null,
        ceremony_start_time: formData.ceremony_start_time || null,
        reception_end_time: formData.reception_end_time || null,
        first_look: formData.first_look || null
      });

      setSubmitted(true);
      toast.success('Intake submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Unable to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Heart className="w-12 h-12 text-[#881337] animate-pulse" />
          <p className="text-[#57534E]">Loading...</p>
        </div>
      </div>
    );
  }

  // No purchase ID or purchase not found
  if (!purchaseId || !purchase) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <nav className="px-6 md:px-12 py-6 flex justify-between items-center border-b border-stone-100">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-2xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </nav>
        <section className="py-20 px-6 md:px-12">
          <div className="max-w-xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="text-2xl font-serif text-[#1C1917] mb-4">Purchase Not Found</h1>
            <p className="text-[#57534E] mb-8">
              We couldn't find your purchase. Please check your confirmation email for the correct link, or purchase the Wedding Insight.
            </p>
            <Button
              onClick={() => navigate('/wedding-insight')}
              className="rounded-full px-8 py-4 bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              Get the Wedding Insight
            </Button>
          </div>
        </section>
      </div>
    );
  }

  // PROMO CODE COMPATIBILITY: If Stripe redirected here with a valid purchase_id,
  // the checkout was successful. "pending" status means webhook hasn't fired yet
  // (common with 100% off promo codes). Allow form submission.
  // Only block if status indicates actual failure.
  const blockedStatuses = ['checkout_failed', 'cancelled', 'refunded'];
  if (purchase.status && blockedStatuses.includes(purchase.status)) {
    return (
      <div className="min-h-screen bg-[#FAF9F7]">
        <nav className="px-6 md:px-12 lg:px-24 py-8">
          <div className="flex items-center gap-2 cursor-pointer w-fit" onClick={() => navigate('/')}>
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif tracking-tight text-[#2C2825]">VowFlow</span>
          </div>
        </nav>
        <section className="py-20 px-6 md:px-12">
          <div className="max-w-xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="font-serif text-2xl text-[#2C2825] mb-4">Purchase Issue</h1>
            <p className="text-[#5C5652] mb-8">
              There was an issue with your purchase. Please try again or contact support.
            </p>
            <Button
              onClick={() => navigate('/wedding-insight')}
              className="px-8 py-4 bg-[#881337] hover:bg-[#6F0F2D] text-white"
            >
              Return to Wedding Insight
            </Button>
          </div>
        </section>
      </div>
    );
  }

  // Already submitted
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <nav className="px-6 md:px-12 py-6 flex justify-between items-center border-b border-stone-100">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-2xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </nav>
        <section className="py-20 px-6 md:px-12">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 
              data-testid="intake-success-heading"
              className="text-3xl md:text-4xl font-serif text-[#1C1917] mb-4"
            >
              Intake Submitted
            </h1>
            <p className="text-lg text-[#57534E] mb-4">
              Thank you! Your planning details have been received.
            </p>
            <p className="text-[#57534E] mb-10">
              We'll review your plan and send your Wedding Insight to <span className="font-medium">{purchase.email}</span> within 5 business days.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="rounded-full px-8 py-4 bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              Back to VowFlow
            </Button>
            
            {/* Soft nudge */}
            <div className="mt-12 p-6 bg-stone-50 rounded-2xl border border-stone-100">
              <p className="text-sm text-[#57534E]">
                If you want ongoing planning support beyond this review, <a href="/signup" className="text-[#881337] hover:underline">VowFlow</a> is available whenever you're ready.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Navigation */}
      <nav className="px-6 md:px-12 py-6 flex justify-between items-center border-b border-stone-100">
        <div 
          className="flex items-center gap-2 cursor-pointer" 
          onClick={() => navigate('/')}
          data-testid="nav-logo"
        >
          <Heart className="text-[#881337]" size={28} fill="#881337" />
          <span className="text-2xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
        </div>
      </nav>

      {/* Form Section */}
      <section className="py-12 md:py-20 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h1 
              data-testid="intake-heading"
              className="text-3xl md:text-4xl font-serif font-light text-[#1C1917] mb-4"
            >
              Wedding Insight Intake
            </h1>
            <p className="text-[#57534E]">
              Tell us about your wedding plans so we can provide personalized insights.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Info */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1C1917] mb-6">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Full Name <span className="text-[#881337]">*</span>
                  </label>
                  <Input
                    data-testid="intake-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Email <span className="text-[#881337]">*</span>
                  </label>
                  <Input
                    data-testid="intake-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="rounded-xl"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Wedding Details */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1C1917] mb-6">Wedding Details</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#57534E] mb-2">
                      Wedding Date <span className="text-[#881337]">*</span>
                    </label>
                    <Input
                      data-testid="intake-wedding-date"
                      type="date"
                      value={formData.wedding_date}
                      onChange={(e) => setFormData({...formData, wedding_date: e.target.value})}
                      className="rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#57534E] mb-2">
                      Estimated Guest Count <span className="text-[#881337]">*</span>
                    </label>
                    <Input
                      data-testid="intake-guest-count"
                      type="number"
                      min="1"
                      value={formData.guest_count}
                      onChange={(e) => setFormData({...formData, guest_count: e.target.value})}
                      className="rounded-xl"
                      placeholder="e.g., 150"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Venue Name + City/State <span className="text-[#881337]">*</span>
                  </label>
                  <Input
                    data-testid="intake-venue"
                    type="text"
                    value={formData.venue}
                    onChange={(e) => setFormData({...formData, venue: e.target.value})}
                    className="rounded-xl"
                    placeholder="e.g., The Grand Ballroom, Austin, TX"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Planning Stage */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1C1917] mb-6">Planning Stage <span className="text-[#881337]">*</span></h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PLANNING_STAGES.map((stage) => (
                  <button
                    key={stage}
                    type="button"
                    data-testid={`planning-stage-${stage.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => setFormData({...formData, planning_stage: stage})}
                    className={`p-4 rounded-xl border text-sm font-medium transition-all ${
                      formData.planning_stage === stage
                        ? 'bg-[#881337] text-white border-[#881337]'
                        : 'bg-white text-[#57534E] border-stone-200 hover:border-[#881337]'
                    }`}
                  >
                    {stage}
                  </button>
                ))}
              </div>
            </div>

            {/* Vendors Booked */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1C1917] mb-2">Vendors Already Booked</h2>
              <p className="text-sm text-[#A8A29E] mb-6">Select all that apply</p>
              <div className="flex flex-wrap gap-2">
                {VENDOR_OPTIONS.map((vendor) => (
                  <button
                    key={vendor}
                    type="button"
                    data-testid={`vendor-${vendor.toLowerCase().replace(/[\/\s]/g, '-')}`}
                    onClick={() => handleVendorToggle(vendor)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      formData.vendors_booked.includes(vendor)
                        ? 'bg-[#881337] text-white'
                        : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                    }`}
                  >
                    {vendor}
                  </button>
                ))}
              </div>
            </div>

            {/* Concerns */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1C1917] mb-6">Your Concerns</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Biggest concern right now <span className="text-[#881337]">*</span>
                  </label>
                  <textarea
                    data-testid="intake-biggest-concern"
                    value={formData.biggest_concern}
                    onChange={(e) => setFormData({...formData, biggest_concern: e.target.value})}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#881337]/20 focus:border-[#881337] resize-none"
                    rows={3}
                    placeholder="What's keeping you up at night about your wedding planning?"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Anything you want us to pay attention to
                  </label>
                  <textarea
                    data-testid="intake-additional-notes"
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#881337]/20 focus:border-[#881337] resize-none"
                    rows={3}
                    placeholder="Any specific areas you'd like us to focus on, or details we should know?"
                  />
                </div>
              </div>
            </div>

            {/* Optional Details */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1C1917] mb-2">Optional Details</h2>
              <p className="text-sm text-[#A8A29E] mb-6">If you know these, they help us give better insights</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Ceremony Start Time
                  </label>
                  <Input
                    data-testid="intake-ceremony-time"
                    type="time"
                    value={formData.ceremony_start_time}
                    onChange={(e) => setFormData({...formData, ceremony_start_time: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    Reception End Time
                  </label>
                  <Input
                    data-testid="intake-reception-time"
                    type="time"
                    value={formData.reception_end_time}
                    onChange={(e) => setFormData({...formData, reception_end_time: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    First Look?
                  </label>
                  <select
                    data-testid="intake-first-look"
                    value={formData.first_look}
                    onChange={(e) => setFormData({...formData, first_look: e.target.value})}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#881337]/20 focus:border-[#881337] bg-white text-[#1C1917]"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                data-testid="submit-intake-btn"
                type="submit"
                disabled={submitting}
                className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white text-lg shadow-md hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Intake'} 
                {!submitting && <ArrowRight className="ml-2 inline" size={20} />}
              </Button>
              <p className="text-center text-sm text-[#A8A29E] mt-4">
                You'll receive your Wedding Insight within 5 business days.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 bg-[#FAFAF9] border-t border-stone-100 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="text-[#881337]" size={20} fill="#881337" />
            <span className="text-lg font-serif font-bold text-[#1C1917]">VowFlow</span>
          </div>
          <p className="text-sm text-[#A8A29E]">
            © {new Date().getFullYear()} VowFlow. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WeddingInsightIntakePage;
